import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ModuleType, PageType, SubPageType } from 'src/app/shared/enums/page-type.enum';
import { ModalService } from 'src/app/shared/services/modal.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { StorageService } from 'src/app/shared/services/storage.service';
import { ContactUsModalComponent } from '../components/_modals/contact-us-modal/contact-us-modal.component';
import { MathHelper } from '../helpers/math.helper';
import { DataAvailabilityInfo } from '../types/data-availability-info.type';
import { Data } from '../types/data.type';
import { BadgeType, MenuSection, MenuSettings } from '../types/menu-section.type';
import { AuthService } from './auth.service';
import { NotificationsService } from './notifications.service';
import { MonitoringStatisticsService } from './monitoring-statistics.service';
import { PermissionHelper } from '../helpers/permission.helper';
import { AppConfigurationService } from './app-configuration.service';
import { Permission } from '../enums/generated.enums';
import { CalendarEventService } from '../../modules/orchestration/calendar/calendar-event.service';
import { TenantsService } from './tenants.service';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private menuSettingsSubject = new BehaviorSubject<MenuSettings>({ sections: [], showTenants: false });

  get menuSections(): MenuSection[] {
    return this.menuSettingsSubject.getValue().sections;
  }

  get menuSettings$(): Observable<MenuSettings> {
    return this.menuSettingsSubject.asObservable();
  }

  constructor(
    private navigationService: NavigationService,
    private modalService: ModalService,
    private storageService: StorageService,
    private authService: AuthService,
    private notificationService: NotificationsService,
    private statisticsService: MonitoringStatisticsService,
    private calendarEventService: CalendarEventService,
    private appConfigurationService: AppConfigurationService,
    private data: Data,
    private tenantsService: TenantsService,
  ) {}

  clearMenuItems(): void {
    this.menuSettingsSubject.next({ sections: [], showTenants: false });
  }

  public initializeMainMenuItems(dataAvailability: DataAvailabilityInfo): void {
    let menuSections: MenuSection[] = [
      {
        label: 'Workflows',
        type: 'items',
        items: [
          {
            route: this.navigationService.getWorkflowsRoute(PageType.Workflows),
            label: 'Workflows',
            icon: 'processes-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Workflows, PageType.Workflows),
            featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Workflows, PageType.Workflows),
            pageType: PageType.Workflows,
            hide: !this.tenantsService.showWorkflows,
          },
          {
            route: this.navigationService.getWorkflowsRoute(PageType.WorkTeams),
            label: 'Work teams',
            icon: 'account-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Workflows, PageType.WorkTeams),
            featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Workflows, PageType.WorkTeams),
            pageType: PageType.WorkTeams,
            hide: !this.tenantsService.showWorkflows,
          },
          {
            route: this.navigationService.getWorkflowsRoute(PageType.WorkflowApis),
            label: 'APIs',
            icon: 'api-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Workflows, PageType.WorkflowApis),
            pageType: PageType.WorkflowApis,
            hide: !this.tenantsService.showWorkflows,
          },
          {
            route: this.navigationService.getWorkflowsRoute(PageType.WorkflowForms),
            label: 'Forms',
            icon: 'sessions-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Workflows, PageType.WorkflowForms),
            featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Workflows, PageType.WorkflowForms),
            pageType: PageType.WorkflowForms,
            hide: !this.tenantsService.showWorkflows,
          },
        ],
      },
      {
        label: 'Monitoring',
        type: 'items',
        items: [
          {
            route: this.navigationService.getMonitoringRoute(PageType.Processes),
            label: 'Processes',
            icon: 'processes-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Monitoring, PageType.Processes),
            pageType: PageType.Processes,
            badge$: this.statisticsService.statistics$.pipe(map(s => s?.quarantinedProcessCount)),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.Resources),
            label: 'Machines ',
            icon: 'resources-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Monitoring, PageType.Resources),
            pageType: PageType.Resources,
            badge$: this.statisticsService.statistics$.pipe(map(s => s?.quarantinedResourceCount)),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.Sessions),
            label: 'Process runs',
            icon: 'sessions-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Monitoring, PageType.Sessions),
            pageType: PageType.Sessions,
          },
          {
            route: this.navigationService.getUtilizationRoute(this.storageService.activeUtilizationSubPage),
            label: 'Utilization',
            icon: 'utilization-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Monitoring, PageType.Utilization),
            pageType: PageType.Utilization,
            onSubPageChange: s => (this.storageService.activeUtilizationSubPage = s as SubPageType.Licenses | SubPageType.Resources),
            tabs: [
              {
                route: this.navigationService.getUtilizationRoute(SubPageType.Licenses),
                label: 'Licenses',
                subPageType: SubPageType.Licenses,
              },
              {
                route: this.navigationService.getUtilizationRoute(SubPageType.Resources),
                label: 'Machines',
                subPageType: SubPageType.Resources,
              },
            ],
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.PastSchedule),
            label: 'Past schedule',
            icon: 'schedule-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Monitoring, PageType.PastSchedule),
            pageType: PageType.PastSchedule,
          },
        ],
      },
      {
        label: 'Orchestration',
        type: 'items',
        items: [
          {
            route: this.navigationService.getAlertsRoute(),
            label: 'Alerts',
            icon: 'alert-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.Alerts),
            pageType: PageType.Alerts,
            badge$: this.notificationService.unreadAlertsCount$,
            badgeType: BadgeType.Warning,
          },
          {
            route: this.navigationService.getCalendarRoute(),
            label: 'Calendar',
            icon: 'calendar-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.Calendar),
            pageType: PageType.Calendar,
            badge$: this.calendarEventService.ongoingMaintenanceWindowsCount$,
            badgeType: BadgeType.Warning,
          },
          {
            route: this.navigationService.getExternalSystemsRoute(),
            label: 'External systems',
            icon: 'external-systems-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.ExternalSystems),
            pageType: PageType.ExternalSystems,
            badge$: this.statisticsService.statistics$.pipe(map(s => s?.quarantinedExternalSystemCount)),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            // FUTURE page - should be hidden
            label: 'Process configuration',
            icon: 'process-config-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.ProcessConfiguration),
            pageType: PageType.ProcessConfiguration,
            disabled: true,
            hide: true,
          },
          {
            route: this.navigationService.getSimulatorRoute(),
            label: 'Simulator',
            icon: 'simulator-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.Simulator),
            pageType: PageType.Simulator,
            tabs: [
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.Simulator),
                label: 'Simulation',
                subPageType: SubPageType.Simulator,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.Processes),
                label: 'Processes',
                subPageType: SubPageType.Processes,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.Sessions),
                label: 'Process runs',
                subPageType: SubPageType.Sessions,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.EventLog),
                label: 'Event log',
                subPageType: SubPageType.EventLog,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.LicenseUtilization),
                label: 'License utilization',
                subPageType: SubPageType.LicenseUtilization,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.ResourceUtilization),
                label: `Machines utilization`,
                subPageType: SubPageType.ResourceUtilization,
              },
              {
                route: this.navigationService.getSimulatorRoute(SubPageType.PastSchedule),
                label: 'Past schedule',
                subPageType: SubPageType.PastSchedule,
              },
            ],
          },
          {
            route: this.navigationService.getDumpViewerRoute(),
            label: 'Dump viewer',
            icon: 'view',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.DumpViewer, SubPageType.DumpViewer),
            featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Orchestration, PageType.DumpViewer, SubPageType.DumpViewer),
            pageType: PageType.DumpViewer,
            tabs: [
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.DumpViewer),
                label: 'Dump viewer',
                subPageType: SubPageType.Simulator,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Processes),
                label: 'Processes',
                subPageType: SubPageType.Processes,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Sessions),
                label: 'Process runs',
                subPageType: SubPageType.Sessions,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.EventLog),
                label: 'Event log',
                subPageType: SubPageType.EventLog,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.PastSchedule),
                label: 'Past schedule',
                subPageType: SubPageType.PastSchedule,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Queues),
                label: 'Queues',
                subPageType: SubPageType.Queues,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Calendar),
                label: 'Calendar',
                subPageType: SubPageType.Calendar,
              },
            ],
          },
        ],
      },
      {
        label: 'Analytics',
        type: 'items',
        items: [
          {
            route: this.navigationService.getDetectedIncidentsRoute(),
            label: 'Detected incidents',
            icon: 'incidents-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Analytics, PageType.DetectedIncidents),
            pageType: PageType.DetectedIncidents,
          },
          {
            route: this.navigationService.getDiagnosticDashboardRoute(),
            label: 'Diagnostic dashboard',
            icon: 'statistics-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Analytics, PageType.DiagnosticDashboard),
            pageType: PageType.DiagnosticDashboard,
          },
          {
            route: this.navigationService.getRuntimeAnalysesRoute(),
            label: 'Runtime analysis',
            icon: 'runtime-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Analytics, PageType.RuntimeAnalysis),
            pageType: PageType.RuntimeAnalysis,
          },
          {
            route: this.navigationService.getAnomaliesRoute(),
            label: 'Anomalies',
            icon: 'anomalies-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Analytics, PageType.Anomalies),
            pageType: PageType.Anomalies,
            badge$: this.notificationService.unreadAnomaliesCount$,
            badgeType: BadgeType.Warning,
          },
        ],
      },
      {
        label: 'Reporting',
        type: 'items',
        items: [
          {
            route: this.navigationService.getExecutiveOverviewRoute(),
            label: 'Executive overview',
            icon: 'executive-overview-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Reporting, PageType.ExecutiveOverview),
            pageType: PageType.ExecutiveOverview,
          },
          {
            route: this.navigationService.getProcessesReportingRoute(SubPageType.ProcessReportList),
            label: 'Process reporting',
            icon: 'process-reporting-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Reporting, PageType.ProcessReporting),
            pageType: PageType.ProcessReporting,
          },
          {
            route: this.navigationService.getMonthlyReportsRoute(),
            label: 'Monthly reports',
            icon: 'reports-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Reporting, PageType.MonthlyReports),
            pageType: PageType.MonthlyReports,
          },
          {
            route: this.navigationService.getBenchmarkOverviewRoute(),
            label: 'Benchmarking',
            icon: 'benchmarking-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Reporting, PageType.BenchmarkOverview),
            pageType: PageType.BenchmarkOverview,
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            route: this.navigationService.getOrganizationSettingsRoute(),
            label: 'Organization settings',
            icon: 'configuration-24',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings),
            pageType: PageType.OrganizationSettings,
            tabs: [
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.GeneralSettings),
                label: 'General',
                subPageType: SubPageType.GeneralSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.TenantsSettings),
                label: 'Tenants',
                subPageType: SubPageType.TenantsSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ProcessesSettings),
                label: 'Processes',
                subPageType: SubPageType.ProcessesSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ResourcesSettings),
                label: 'Machines',
                subPageType: SubPageType.ResourcesSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.QueuesSettings),
                label: 'Queues',
                subPageType: SubPageType.QueuesSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.UsersSettings),
                label: 'Users',
                subPageType: SubPageType.UsersSettings,
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.NotificationsSettings),
                label: 'Email notifications',
                subPageType: SubPageType.NotificationsSettings,
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.NotificationsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ApiCredentialsSettings),
                label: 'API credentials',
                subPageType: SubPageType.ApiCredentialsSettings,
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ApiCredentialsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.MsTeamsSettings),
                label: 'Microsoft Teams',
                subPageType: SubPageType.MsTeamsSettings,
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.MsTeamsSettings),
                featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.MsTeamsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.AnomaliesSettings),
                label: 'Anomalies',
                subPageType: SubPageType.AnomaliesSettings,
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.AnomaliesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ExternalServicesSettings),
                label: 'Integrations',
                subPageType: SubPageType.ExternalServicesSettings,
                group: 'Orchestration',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ExternalServicesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ConditionStates),
                label: 'Condition states',
                subPageType: SubPageType.ConditionStates,
                group: 'Orchestration',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ConditionStates),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.PendingSessions),
                label: 'Pendings',
                subPageType: SubPageType.PendingSessions,
                group: 'Orchestration',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.PendingSessions),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.OrchestrationDebugging),
                label: 'Debugging',
                subPageType: SubPageType.OrchestrationDebugging,
                group: 'Orchestration',
                isHidden: !this.appConfigurationService.isOnPremise && !this.authService.hasPermission(Permission.SystemAdmin),
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.OrchestrationDebugging),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ReportingSettings),
                label: 'Metrics',
                subPageType: SubPageType.ReportingSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ReportingSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BusinessProcessesSettings),
                label: 'Business processes',
                subPageType: SubPageType.BusinessProcessesSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BusinessProcessesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.SavingsSettings),
                label: 'Costs and savings',
                subPageType: SubPageType.SavingsSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.SavingsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.GroupsSettings),
                label: 'Process groups',
                subPageType: SubPageType.GroupsSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.GroupsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BillingGroupsSettings),
                label: 'Billing groups',
                subPageType: SubPageType.BillingGroupsSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BillingGroupsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BenchmarkingSettings),
                label: 'Benchmarking',
                subPageType: SubPageType.BenchmarkingSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BenchmarkingSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ImportExportSettings),
                label: 'Import/Export',
                subPageType: SubPageType.ImportExportSettings,
                group: 'Reporting',
                permissions: PermissionHelper.getPagePermissions(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ImportExportSettings),
              },
            ],
          },
          {
            route: this.navigationService.getAuditLogRoute(),
            label: 'Audit log',
            icon: 'audit-log',
            permissions: PermissionHelper.getPagePermissions(ModuleType.Orchestration, PageType.AuditLog),
            disabled: !dataAvailability.isValid,
            hide: !dataAvailability.isValid,
            pageType: PageType.AuditLog,
          },
          {
            label: 'Help & feedback',
            icon: 'help-24',
            dataTestId: 'help-feedback',
            onClick: () => this.modalService.open(ContactUsModalComponent),
          },
        ],
      },
    ];

    menuSections.forEach(m => (m.items = m.items.filter(i => i.permissions == null || this.authService.hasAnyPermission(i.permissions))));
    menuSections.forEach(m => (m.items = m.items.filter(i => i.featureFlag == null || this.authService.hasFeatureFlag(i.featureFlag))));
    menuSections.forEach(m => (m.items = m.items.filter(i => i.hide !== true)));
    menuSections = menuSections.filter(m => m.items.length > 0);

    if (!dataAvailability.isValid) {
      menuSections = menuSections.filter(m => m.type == 'actions');
    }

    const itemCount = MathHelper.sum(
      menuSections.filter(section => section.type === 'items' || section.type === 'submenu'),
      s => s.items.length,
    );

    this.menuSettingsSubject.next({
      sections: menuSections,
      isMenuExpanded: itemCount < 3 ? false : null,
      showTenants: true,
    });
  }

  public initializeAdminMenuItems(): void {
    const menuSections: MenuSection[] = [
      {
        label: 'Admin',
        type: 'submenu',
        parentRoute: ['/'],
        items: [
          {
            route: this.navigationService.getAdminOrganizationsRoute(),
            label: 'Organizations',
            icon: 'organization-24',
          },
          {
            route: this.navigationService.getAdminUsersRoute(),
            label: 'Users',
            icon: 'account-24',
          },
          {
            route: this.navigationService.getAdminLicensesRoute(),
            label: 'Licenses',
            icon: 'shield-check-24',
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            label: 'Help & feedback',
            icon: 'help-24',
            onClick: () => {
              this.modalService.open(ContactUsModalComponent);
            },
          },
        ],
      },
    ];

    this.menuSettingsSubject.next({ sections: menuSections, showTenants: false });
  }

  public initializeRuntimeAnalysisMenuItems(processId: string): void {
    const menuSections: MenuSection[] = [
      {
        label: 'Runtime analysis',
        type: 'submenu',
        parentRoute: this.navigationService.getRuntimeAnalysesRoute(),
        items: [
          {
            route: this.navigationService.getOverviewRoute(processId),
            label: 'Overview',
            icon: 'overview-24',
          },
          {
            route: this.navigationService.getActionableInsightsRoute(processId),
            label: 'Actionable insights',
            icon: 'insight-24',
            badge$: this.data.actionableInsightsByStateChanged.pipe(map(() => this.data.masterProcess?.actionableInsightsByState.get('')?.size)),
            badgeType: BadgeType.Count,
          },
          {
            route: this.navigationService.getProcessMapRoute(processId),
            label: 'Process map',
            icon: 'map-24',
          },
          {
            route: this.navigationService.getVariantAnalysisRoute(processId),
            label: 'Variant analysis',
            icon: 'variant-analysis-24',
          },
          {
            route: this.navigationService.getCostAnalysisRoute(processId),
            label: 'Cost analysis',
            icon: 'cost-analysis-24',
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            route: [],
            label: 'Assign this analysis',
            icon: 'assign',
            disabled: true,
            hide: true,
          },
          {
            route: [],
            label: 'Analysis settings',
            icon: 'settings',
            disabled: true,
            hide: true,
          },
          {
            label: 'Help & feedback',
            icon: 'help-24',
            onClick: () => {
              this.modalService.open(ContactUsModalComponent);
            },
          },
        ],
      },
    ];

    this.menuSettingsSubject.next({ sections: menuSections, showTenants: false });
  }
}
